import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { clearEmployeeToken } from "@/lib/employeeToken";
import { TRPCClientError } from "@trpc/client";
import { useCallback, useEffect, useMemo, useRef } from "react";

type UseAuthOptions = {
  redirectOnUnauthenticated?: boolean;
  redirectPath?: string;
};

export function useAuth(options?: UseAuthOptions) {
  const { redirectOnUnauthenticated = false, redirectPath } = options ?? {};
  const utils = trpc.useUtils();
  const hasRedirected = useRef(false);

  const meQuery = trpc.auth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
    // Don't refetch on mount if we already have data — prevents unnecessary re-auth
    refetchOnMount: false,
    // Stale time of 5 minutes — don't re-check auth constantly
    staleTime: 5 * 60 * 1000,
  });

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      utils.auth.me.setData(undefined, null);
    },
  });

  const logout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error: unknown) {
      if (
        error instanceof TRPCClientError &&
        error.data?.code === "UNAUTHORIZED"
      ) {
        return;
      }
      throw error;
    } finally {
      clearEmployeeToken();
      // Also clear the OAuth session token (Safari ITP workaround)
      try { localStorage.removeItem("darkhorse_session_token"); } catch {}
      utils.auth.me.setData(undefined, null);
      await utils.auth.me.invalidate();
    }
  }, [logoutMutation, utils]);

  const state = useMemo(() => {
    try {
      localStorage.setItem(
        "manus-runtime-user-info",
        JSON.stringify(meQuery.data)
      );
    } catch {
      // localStorage may be unavailable in some contexts
    }
    return {
      user: meQuery.data ?? null,
      loading: meQuery.isLoading || logoutMutation.isPending,
      error: meQuery.error ?? logoutMutation.error ?? null,
      isAuthenticated: Boolean(meQuery.data),
    };
  }, [
    meQuery.data,
    meQuery.error,
    meQuery.isLoading,
    logoutMutation.error,
    logoutMutation.isPending,
  ]);

  useEffect(() => {
    if (!redirectOnUnauthenticated) return;
    if (meQuery.isLoading || logoutMutation.isPending) return;
    if (state.user) return;
    if (typeof window === "undefined") return;

    // Don't redirect if we're already on the OAuth callback page
    if (window.location.pathname.startsWith("/api/oauth/")) return;

    // Don't redirect if there's an auth_error param (just came back from failed OAuth)
    const params = new URLSearchParams(window.location.search);
    if (params.has("auth_error")) return;

    // Prevent redirect loops — only redirect once per mount
    if (hasRedirected.current) return;
    hasRedirected.current = true;

    // Use provided redirectPath or compute login URL
    // Note: we don't auto-redirect to Manus OAuth anymore.
    // The DashboardLayout shows a LoginScreen with both options.
    // This prevents employees from being sent to Manus OAuth.
    if (redirectPath) {
      window.location.href = redirectPath;
    }
    // If no redirectPath, don't redirect — let the component handle showing login UI
  }, [
    redirectOnUnauthenticated,
    redirectPath,
    logoutMutation.isPending,
    meQuery.isLoading,
    state.user,
  ]);

  return {
    ...state,
    refresh: () => meQuery.refetch(),
    logout,
  };
}
